import React from 'react';
import Icon from '../../../components/AppIcon';

const SustainabilityGoalsTracker = () => {
  const sustainabilityGoals = [
    {
      id: 'carbon-neutral',
      title: 'Carbon Neutral Operations',
      target: '2030',
      progress: 67.3,
      status: 'on-track',
      description: 'Achieve net-zero carbon emissions across all operations',
      milestones: [
        { year: '2024', target: '25% reduction', achieved: true },
        { year: '2026', target: '50% reduction', achieved: false },
        { year: '2028', target: '75% reduction', achieved: false },
        { year: '2030', target: '100% neutral', achieved: false }
      ],
      currentMetric: '23.7% reduction',
      icon: 'Target',
      color: 'success'
    },
    {
      id: 'renewable-energy',
      title: 'Renewable Energy',
      target: '2027',
      progress: 45.8,
      status: 'at-risk',
      description: '100% renewable energy for all facilities',
      milestones: [
        { year: '2024', target: '30% renewable', achieved: true },
        { year: '2025', target: '50% renewable', achieved: false },
        { year: '2026', target: '75% renewable', achieved: false },
        { year: '2027', target: '100% renewable', achieved: false }
      ],
      currentMetric: '28.4% renewable',
      icon: 'Sun',
      color: 'warning'
    },
    {
      id: 'waste-zero',
      title: 'Zero Waste to Landfill',
      target: '2025',
      progress: 87.2,
      status: 'on-track',
      description: 'Divert 100% of operational waste from landfills',
      milestones: [
        { year: '2023', target: '70% diversion', achieved: true },
        { year: '2024', target: '85% diversion', achieved: true },
        { year: '2025', target: '100% diversion', achieved: false }
      ],
      currentMetric: '87.2% diverted',
      icon: 'Recycle',
      color: 'success'
    },
    {
      id: 'water-efficiency',
      title: 'Water Efficiency',
      target: '2026',
      progress: 52.1,
      status: 'on-track',
      description: '40% reduction in water consumption per unit',
      milestones: [
        { year: '2024', target: '15% reduction', achieved: true },
        { year: '2025', target: '25% reduction', achieved: false },
        { year: '2026', target: '40% reduction', achieved: false }
      ],
      currentMetric: '18.3% reduction',
      icon: 'Droplets',
      color: 'primary'
    },
    {
      id: 'sustainable-supply',
      title: 'Sustainable Supply Chain',
      target: '2028',
      progress: 34.7,
      status: 'behind',
      description: '80% of suppliers meet sustainability standards',
      milestones: [
        { year: '2024', target: '30% certified', achieved: false },
        { year: '2026', target: '50% certified', achieved: false },
        { year: '2028', target: '80% certified', achieved: false }
      ],
      currentMetric: '24.1% certified',
      icon: 'Truck',
      color: 'error'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return 'text-success';
      case 'at-risk': return 'text-warning';
      case 'behind': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'on-track': return 'bg-success';
      case 'at-risk': return 'bg-warning';
      case 'behind': return 'bg-error';
      default: return 'bg-muted-foreground';
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'on-track': return '#10B981';
      case 'at-risk': return '#F59E0B';
      case 'behind': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const CircularProgress = ({ progress, color, size = 80 }) => {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="var(--color-muted)"
            strokeWidth="4"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth="4"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-foreground">{progress?.toFixed(0)}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
          <Icon name="Target" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Sustainability Goals Tracker</h3>
          <p className="text-sm text-muted-foreground">Progress toward environmental targets</p>
        </div>
      </div>
      {/* Goals List */}
      <div className="space-y-6">
        {sustainabilityGoals?.map((goal) => (
          <div key={goal?.id} className="border border-border rounded-lg p-4 hover-lift">
            {/* Goal Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-3">
                <div className={`flex items-center justify-center w-10 h-10 bg-${goal?.color}/10 rounded-lg`}>
                  <Icon name={goal?.icon} size={20} color={`var(--color-${goal?.color})`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-foreground">{goal?.title}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goal?.status)} bg-muted/50`}>
                      {goal?.status?.replace('-', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{goal?.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Target: {goal?.target}</span>
                    <span>Current: {goal?.currentMetric}</span>
                  </div>
                </div>
              </div>
              <CircularProgress 
                progress={goal?.progress} 
                color={getProgressColor(goal?.status)}
                size={60}
              />
            </div>

            {/* Milestones */}
            <div className="ml-13">
              <h5 className="text-sm font-medium text-foreground mb-3">Milestones</h5>
              <div className="flex items-center space-x-1">
                {goal?.milestones?.map((milestone, index) => (
                  <React.Fragment key={milestone?.year}>
                    <div className="flex flex-col items-center">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        milestone?.achieved 
                          ? `${getStatusBg(goal?.status)} border-transparent` 
                          : 'border-muted-foreground bg-background'
                      }`}>
                        {milestone?.achieved && (
                          <Icon name="Check" size={12} color="white" className="w-full h-full" />
                        )}
                      </div>
                      <div className="text-xs text-center mt-1">
                        <div className="font-medium text-foreground">{milestone?.year}</div>
                        <div className="text-muted-foreground">{milestone?.target}</div>
                      </div>
                    </div>
                    {index < goal?.milestones?.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        milestone?.achieved ? getStatusBg(goal?.status) : 'bg-muted'
                      }`}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-success">3</p>
            <p className="text-sm text-muted-foreground">On Track</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-warning">1</p>
            <p className="text-sm text-muted-foreground">At Risk</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-error">1</p>
            <p className="text-sm text-muted-foreground">Behind</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SustainabilityGoalsTracker;